<script setup lang="ts">
/**
 * Créneaux de collecte : structure des plages de retrait et découpage en
 * créneaux, branchés sur le serveur de schedules (apps/server externe,
 * localhost:8081 — endpoints restaurants/{storeId}/schedules/*). Chaque
 * changement enregistre automatiquement via PUT .../schedules/weekday-schedule
 * (jamais POST .../schedules/add) — pas de bouton "Enregistrer" séparé. Le
 * target envoyé dépend de cfg.basis : 'open' (Horaires d'ouverture) écrit
 * sur target=store — les vraies heures d'ouverture du commerce, cohérent
 * puisque les plages affichées dans ce mode en sont déjà la copie exacte —
 * 'custom' (Plages personnalisées) écrit sur target=click_and_collect.
 * cfg.basis === 'open' et cfg.slice (découpage en créneaux actif) sont en
 * plus persistés en tant que schedule.use_store_hours / schedule.pickup_by_range
 * via l'endpoint générique restaurants/{storeId}/settings
 * (StoreSettingsService). pickup_by_range est l'INVERSE de cfg.slice :
 * true = le client choisit une plage libre (découpage désactivé), false =
 * créneaux précis (découpage actif). cfg.mode ('asap'|'scheduled') est de
 * même persisté en tant que schedule.availability_rule
 * ('preparation_time'|'order_cutoff'). Combinaisons restent en mock local,
 * non envoyées au serveur.
 */
import { ref, reactive, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  StoreScheduleService,
  type TWeekday as TApiWeekday,
  type IStoreSchedule,
} from '@/services/store-schedule.services';
import { StoreSettingsService } from '@/services/store-settings.services';

const props = defineProps<{ storeId: string }>();

const { t } = useI18n({ useScope: 'global' });

type TWeekday = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
const WD7: TWeekday[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
function wdShort(d: TWeekday): string {
  return t(`collectSlots.weekdayShort.${d}`);
}

interface IOpeningWindow {
  open: string;
  close: string;
}
interface IRange {
  id: string;
  from: string;
  to: string;
}
interface IScheduledDay {
  enabled: boolean;
  // Nombre de jours calendaires avant le jour de retrait où la commande doit
  // être payée (ex. retrait lundi, cutoffDays=2 → date limite samedi).
  cutoffDays: number;
  cutoffTime: string;
}
interface ICombo {
  id: string;
  orderFrom: string;
  orderTo: string;
  pickFrom: string;
  pickTo: string;
}

// ── Horaires d'ouverture du commerce (créneaux "regular", chargés depuis
// restaurants/{storeId}/schedules/search) ────────────────────────────────────
const WD_TO_API: Record<TWeekday, TApiWeekday> = {
  mon: 'monday',
  tue: 'tuesday',
  wed: 'wednesday',
  thu: 'thursday',
  fri: 'friday',
  sat: 'saturday',
  sun: 'sunday',
};

const openingHours = reactive<Record<TWeekday, IOpeningWindow[]>>({
  mon: [],
  tue: [],
  wed: [],
  thu: [],
  fri: [],
  sat: [],
  sun: [],
});

const loading = ref(true);
const loadError = ref('');
const saving = ref(false);
const saveError = ref('');
const saved = ref(false);

// Peuple cfg.rangesByDay (plages personnalisées) à partir de schedules
// bruts — réutilisé au chargement initial ET quand l'utilisateur bascule
// vers "Plages personnalisées" en cours de session (voir selectBasis), pour
// ne jamais laisser rangesByDay vide alors que le serveur a de vraies
// plages, ce qui écraserait silencieusement l'auto-save suivant.
function populateRangesByDay(schedules: IStoreSchedule[]) {
  for (const d of WD7) cfg.rangesByDay[d] = [];
  for (const s of schedules) {
    if (s.type !== 'regular' || !s.weekday || s.isClosed) continue;
    if (!s.openTime || !s.closeTime) continue;
    const localDay = (
      Object.entries(WD_TO_API) as [TWeekday, TApiWeekday][]
    ).find(([, api]) => api === s.weekday)?.[0];
    if (!localDay) continue;
    (cfg.rangesByDay[localDay] ??= []).push({
      id: `open-${localDay}-${s.id}`,
      from: s.openTime.slice(0, 5),
      to: s.closeTime.slice(0, 5),
    });
  }
}

async function loadSchedules() {
  loading.value = true;
  loadError.value = '';
  try {
    // Les horaires d'ouverture affichés en base "Horaires d'ouverture"
    // viennent toujours de target=store (les vraies heures d'ouverture du
    // commerce). Les réglages de collecte (découpage, capacité, cutoff) sont
    // en revanche lus depuis le MÊME target que celui utilisé à la
    // sauvegarde (voir saveSchedules) : store si cfg.basis === 'open',
    // click_and_collect si 'custom' — connu seulement une fois settingsRes
    // résolu, d'où le choix de la source après le Promise.all plutôt qu'un
    // 2e appel schedules/search redondant.
    const [storeRes, storeHoursRes, cncSchedulesRes, settingsRes] =
      await Promise.all([
        StoreScheduleService.getPreparationTimeBuffer(props.storeId),
        StoreScheduleService.searchSchedules(props.storeId, 'store'),
        StoreScheduleService.searchSchedules(
          props.storeId,
          'click_and_collect',
        ),
        StoreSettingsService.getStoreSettings(props.storeId),
      ]);
    const settingsData = settingsRes.data?.data ?? settingsRes.data;
    const categories = settingsData?.categories ?? [];
    const flatValues: Record<string, unknown> = {};
    for (const cat of categories) {
      for (const s of cat.settings ?? []) flatValues[s.key] = s.value;
    }
    cfg.basis = flatValues['schedule.use_store_hours'] ? 'open' : 'custom';
    // pickup_by_range=true veut dire "le client choisit une plage libre",
    // donc découpage désactivé — inverse de cfg.slice.
    cfg.slice = !flatValues['schedule.pickup_by_range'];

    for (const d of WD7) openingHours[d] = [];
    for (const s of storeHoursRes.data.data ?? []) {
      if (s.type !== 'regular' || !s.weekday || s.isClosed) continue;
      const localDay = (
        Object.entries(WD_TO_API) as [TWeekday, TApiWeekday][]
      ).find(([, api]) => api === s.weekday)?.[0];
      if (!localDay || !s.openTime || !s.closeTime) continue;
      openingHours[localDay].push({ open: s.openTime, close: s.closeTime });
    }

    const settingsSourceSchedules =
      cfg.basis === 'open'
        ? (storeHoursRes.data.data ?? [])
        : (cncSchedulesRes.data.data ?? []);
    // En mode "Plages personnalisées", ces mêmes lignes SONT les plages —
    // sans ce peuplement, cfg.rangesByDay restait vide après un rechargement
    // même quand le serveur avait bien plusieurs plages par jour enregistrées.
    if (cfg.basis === 'custom') {
      populateRangesByDay(settingsSourceSchedules);
    }
    let anyScheduled = false;
    for (const s of settingsSourceSchedules) {
      if (s.type !== 'regular' || !s.weekday || s.isClosed) continue;
      const localDay = (
        Object.entries(WD_TO_API) as [TWeekday, TApiWeekday][]
      ).find(([, api]) => api === s.weekday)?.[0];
      if (!localDay) continue;

      // Réglages globaux (découpage/capacité) : première valeur rencontrée
      // comme représentative, puisque l'UI n'a qu'un seul réglage pour
      // toute la semaine. cfg.slice lui-même vient de schedule.pickup_by_range
      // (settings), pas d'ici — un slotIntervalMinutes peut rester enregistré
      // côté schedule même quand le découpage est désactivé.
      if (typeof s.slotIntervalMinutes === 'number') {
        cfg.step = s.slotIntervalMinutes as typeof cfg.step;
      }
      if (typeof s.maxOrdersPerSlot === 'number' && s.maxOrdersPerSlot > 0) {
        cfg.capacity.enabled = true;
        cfg.capacity.max = s.maxOrdersPerSlot;
      }
      // Réglage par jour : coupure de commande programmée.
      if (
        typeof s.orderCutoffDays === 'number' &&
        s.orderCutoffDays > 0 &&
        s.orderCutoffTime
      ) {
        anyScheduled = true;
        cfg.scheduled.byDay[localDay] = {
          enabled: true,
          cutoffDays: s.orderCutoffDays,
          cutoffTime: s.orderCutoffTime.slice(0, 5),
        };
      }
    }
    // schedule.availability_rule fait foi sur le mode (ASAP vs programmé) —
    // le déduire de anyScheduled reste un repli si la clé est absente.
    const availabilityRule = flatValues['schedule.availability_rule'];
    if (availabilityRule === 'order_cutoff') {
      cfg.mode = 'scheduled';
    } else if (availabilityRule === 'preparation_time') {
      cfg.mode = 'asap';
    } else if (anyScheduled) {
      cfg.mode = 'scheduled';
    }
    const buffer = storeRes.data.data?.preparationTimeBuffer;
    if (typeof buffer === 'number') cfg.asap.prep = buffer;
  } catch {
    loadError.value = t('collectSlots.loadError');
  } finally {
    loading.value = false;
  }
}

async function saveSchedules() {
  saving.value = true;
  saveError.value = '';
  saved.value = false;
  try {
    // Horaires d'ouverture → écrit/synchronise les vraies heures du commerce
    // (target=store) ; Plages personnalisées → reste propre aux créneaux de
    // collecte (target=click_and_collect).
    const saveTarget = cfg.basis === 'open' ? 'store' : 'click_and_collect';
    const payload: Record<string, unknown> = { target: saveTarget };
    for (const d of WD7) {
      payload[WD_TO_API[d]] = effRangesForDay(d)
        .filter((r) => t2m(r.to) > t2m(r.from))
        .map((r) => ({
          open: r.from,
          close: r.to,
          slotIntervalMinutes: cfg.slice ? cfg.step : undefined,
          maxOrdersPerSlot: cfg.capacity.enabled ? cfg.capacity.max : undefined,
          orderCutoffDays:
            cfg.mode === 'scheduled' && scheduledDayOf(d).enabled
              ? scheduledDayOf(d).cutoffDays
              : undefined,
          orderCutoffTime:
            cfg.mode === 'scheduled' && scheduledDayOf(d).enabled
              ? scheduledDayOf(d).cutoffTime
              : undefined,
        }));
    }
    await Promise.all([
      StoreScheduleService.updateWeekdaySchedule(props.storeId, payload),
      cfg.mode === 'asap'
        ? StoreScheduleService.updatePreparationTimeBuffer(
            props.storeId,
            cfg.asap.prep,
          )
        : Promise.resolve(),
      StoreSettingsService.updateStoreSettings(props.storeId, {
        'schedule.use_store_hours': cfg.basis === 'open',
        'schedule.pickup_by_range': !cfg.slice,
        'schedule.availability_rule':
          cfg.mode === 'asap' ? 'preparation_time' : 'order_cutoff',
      }),
    ]);
    saved.value = true;
    setTimeout(() => {
      saved.value = false;
    }, 2500);
  } catch (err: any) {
    const msgs = err?.response?.data?.message;
    saveError.value = Array.isArray(msgs)
      ? msgs.join(' · ')
      : msgs || t('collectSlots.saveError');
  } finally {
    saving.value = false;
  }
}

// ── Enregistrement automatique : chaque changement de champ sauvegarde,
// débouncé pour ne pas spammer le serveur pendant une saisie continue —
// activé plus bas, une fois `cfg` déclaré (voir fin de fichier).
const autoSaveReady = ref(false);

// Bascule Horaires d'ouverture ↔ Plages personnalisées, appelée par le
// template au clic. En arrivant sur "custom" alors que rangesByDay n'a
// jamais été peuplé pour ce mode (chargement initial en 'open'), on
// recharge target=click_and_collect avant d'autoriser l'auto-save —
// sinon effRangesForDay('custom') renvoie [] et le prochain auto-save
// écrase silencieusement les vraies plages déjà enregistrées côté serveur.
async function selectBasis(basis: 'open' | 'custom') {
  cfg.basis = basis;
  if (basis !== 'custom') return;
  const alreadyPopulated = WD7.some((d) => (cfg.rangesByDay[d] ?? []).length);
  if (alreadyPopulated) return;
  autoSaveReady.value = false;
  try {
    const res = await StoreScheduleService.searchSchedules(
      props.storeId,
      'click_and_collect',
    );
    populateRangesByDay(res.data.data ?? []);
  } finally {
    autoSaveReady.value = true;
  }
}
let autoSaveTimer: ReturnType<typeof setTimeout> | null = null;

function randSuffix() {
  return Math.random().toString(36).slice(2, 6);
}

// ── Configuration (mock local — persistée nulle part pour l'instant) ─────────
const cfg = reactive({
  basis: 'open' as 'open' | 'custom',
  mode: 'asap' as 'asap' | 'scheduled',
  rangesByDay: {} as Partial<Record<TWeekday, IRange[]>>,
  autoParts: 2,
  slice: true,
  step: 15 as 10 | 15 | 20 | 30 | 60,
  asap: { prep: 20 },
  scheduled: {
    byDay: {} as Partial<Record<TWeekday, IScheduledDay>>,
  },
  capacity: { enabled: false, max: 8 },
  combos: [] as ICombo[],
});

const SLOT_BASIS = [
  {
    id: 'open' as const,
    icon: 'pi-clock',
    labelKey: 'collectSlots.basisOpenLabel',
    descKey: 'collectSlots.basisOpenDesc',
  },
  {
    id: 'custom' as const,
    icon: 'pi-clipboard',
    labelKey: 'collectSlots.basisCustomLabel',
    descKey: 'collectSlots.basisCustomDesc',
  },
];
const SLOT_MODES = [
  {
    id: 'asap' as const,
    icon: 'pi-clock',
    labelKey: 'collectSlots.modeAsapLabel',
    descKey: 'collectSlots.modeAsapDesc',
  },
  {
    id: 'scheduled' as const,
    icon: 'pi-calendar',
    labelKey: 'collectSlots.modeScheduledLabel',
    descKey: 'collectSlots.modeScheduledDesc',
  },
];
const STEP_OPTIONS = [10, 15, 20, 30, 60] as const;

function t2m(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}
function slotCountFor(from: string, to: string, step: number): number {
  const diff = t2m(to) - t2m(from);
  return diff > 0 ? Math.floor(diff / step) : 0;
}

const openDays = computed(() => WD7.filter((d) => openingHours[d].length));

function effRangesForDay(d: TWeekday): IRange[] {
  if (cfg.basis === 'open') {
    return openingHours[d].map((w, i) => ({
      id: `open-${d}-${i}`,
      from: w.open,
      to: w.close,
    }));
  }
  return cfg.rangesByDay[d] ?? [];
}

function defaultRangeFor(d: TWeekday): { from: string; to: string } {
  const w = openingHours[d][0];
  return w ? { from: w.open, to: w.close } : { from: '09:00', to: '12:00' };
}

function addRange(d: TWeekday) {
  const list = cfg.rangesByDay[d] ?? (cfg.rangesByDay[d] = []);
  list.push({ id: `r${d}${randSuffix()}`, ...defaultRangeFor(d) });
}
function removeRange(d: TWeekday, id: string) {
  cfg.rangesByDay[d] = (cfg.rangesByDay[d] ?? []).filter((r) => r.id !== id);
}

function autoSplitRanges() {
  const parts = Math.max(1, Math.min(12, cfg.autoParts));
  for (const d of WD7) {
    const w = openingHours[d][0];
    if (!w) {
      cfg.rangesByDay[d] = [];
      continue;
    }
    const start = t2m(w.open);
    const end = t2m(w.close);
    const span = Math.max(0, end - start);
    const chunk = span / parts;
    const ranges: IRange[] = [];
    for (let i = 0; i < parts; i++) {
      const from = start + Math.round(chunk * i);
      const to = i === parts - 1 ? end : start + Math.round(chunk * (i + 1));
      ranges.push({
        id: `r${d}${randSuffix()}`,
        from: minutesToHHMM(from),
        to: minutesToHHMM(to),
      });
    }
    cfg.rangesByDay[d] = ranges;
  }
}
function minutesToHHMM(mins: number): string {
  const h = Math.floor(mins / 60)
    .toString()
    .padStart(2, '0');
  const m = (mins % 60).toString().padStart(2, '0');
  return `${h}:${m}`;
}

function dayCount(d: TWeekday): number {
  return effRangesForDay(d).reduce(
    (acc, r) =>
      acc +
      (t2m(r.to) > t2m(r.from) ? slotCountFor(r.from, r.to, cfg.step) : 0),
    0,
  );
}

function scheduledDayOf(d: TWeekday): IScheduledDay {
  return (
    cfg.scheduled.byDay[d] ??
    (cfg.scheduled.byDay[d] = {
      enabled: false,
      cutoffDays: 1,
      cutoffTime: '18:00',
    })
  );
}
// Jour de semaine où tombe la date limite, pour affichage seul (ex.
// retrait lundi, cutoffDays=2 → "commander avant samedi").
function cutoffWeekday(d: TWeekday, cutoffDays: number): TWeekday {
  const idx = (WD7.indexOf(d) - cutoffDays + 7 * 10) % 7;
  return WD7[idx];
}

function addCombo() {
  cfg.combos.push({
    id: `cb${randSuffix()}`,
    orderFrom: '08:00',
    orderTo: '11:00',
    pickFrom: '12:00',
    pickTo: '14:00',
  });
}
function removeCombo(id: string) {
  cfg.combos = cfg.combos.filter((c) => c.id !== id);
}

const capacityUnit = computed(() =>
  cfg.slice
    ? t('collectSlots.capacityUnitSlot')
    : t('collectSlots.capacityUnitRange'),
);

onMounted(async () => {
  await loadSchedules();
  // Le chargement initial peuple cfg/openingHours (déclenche le watch
  // ci-dessous) — on n'active l'auto-save qu'une fois cet état stabilisé,
  // sinon le premier rendu déclencherait une sauvegarde inutile.
  autoSaveReady.value = true;
});
watch(
  () => props.storeId,
  async () => {
    autoSaveReady.value = false;
    await loadSchedules();
    autoSaveReady.value = true;
  },
);
watch(
  [cfg, openingHours],
  () => {
    if (!autoSaveReady.value) return;
    if (autoSaveTimer) clearTimeout(autoSaveTimer);
    autoSaveTimer = setTimeout(() => {
      saveSchedules();
    }, 600);
  },
  { deep: true },
);
</script>

<template>
  <div class="cs">
    <p class="cs__intro">
      {{ t('collectSlots.intro1') }}
      <em>{{ t('collectSlots.introRetrieve') }}</em>
      {{ t('collectSlots.intro2') }}
      <b>{{ t('collectSlots.introSlotType') }}</b>
      {{ t('collectSlots.intro3') }}
    </p>

    <div v-if="loading" class="cs__loading">
      <i class="pi pi-spin pi-spinner" />
      {{ t('collectSlots.loading') }}
    </div>
    <div v-else-if="loadError" class="cs__banner cs__banner--error">
      <i class="pi pi-exclamation-triangle" />
      {{ loadError }}
    </div>

    <template v-else>
      <!-- 1 · Structure des créneaux -->
      <div class="cs__param">
        <div class="cs__param-h">{{ t('collectSlots.step1Title') }}</div>

        <div class="cs__sub-lbl">{{ t('collectSlots.pickupRanges') }}</div>
        <div class="cs__types">
          <button
            v-for="b in SLOT_BASIS"
            :key="b.id"
            type="button"
            class="cs__type"
            :class="{ 'cs__type--on': cfg.basis === b.id }"
            @click="selectBasis(b.id)"
          >
            <span class="cs__type-ic"><i :class="['pi', b.icon]" /></span>
            <span class="cs__type-tx">
              <b>{{ t(b.labelKey) }}</b>
              <span>{{ t(b.descKey) }}</span>
            </span>
          </button>
        </div>

        <div class="cs__editor">
          <!-- Basé sur les horaires d'ouverture -->
          <div v-if="cfg.basis === 'open'" class="cs__sec">
            <p class="cs__sub">
              {{ t('collectSlots.followsOpeningPre') }}
              <b>{{ t('collectSlots.followsOpeningBold') }}</b>
              {{ t('collectSlots.followsOpeningPost') }}
            </p>
            <div class="cs__preview">
              <div v-for="d in openDays" :key="d" class="cs__sched-row">
                <span class="cs__d">{{ wdShort(d) }}</span>
                <span class="cs__muted">{{
                  openingHours[d].map((w) => `${w.open}–${w.close}`).join(' · ')
                }}</span>
              </div>
            </div>
          </div>

          <!-- Plages personnalisées -->
          <div v-else class="cs__sec">
            <div class="cs__autosplit">
              <i class="pi pi-bolt" style="font-size: 0.8125rem" />
              <span>{{ t('collectSlots.splitInto') }}</span>
              <input
                v-model.number="cfg.autoParts"
                type="number"
                min="1"
                max="12"
                class="cs__ninp"
              />
              <span>{{ t('collectSlots.equalRanges') }}</span>
              <button
                type="button"
                class="cs__btn cs__btn--sm"
                @click="autoSplitRanges"
              >
                {{ t('collectSlots.generate') }}
              </button>
            </div>
            <p class="cs__sub" style="margin: 10px 0 8px">
              {{ t('collectSlots.rangesPerDay') }}
            </p>
            <div v-for="d in WD7" :key="d" class="cs__day">
              <div class="cs__day-h">{{ wdShort(d) }}</div>
              <div class="cs__ranges">
                <template v-if="(cfg.rangesByDay[d] ?? []).length">
                  <div
                    v-for="r in cfg.rangesByDay[d]"
                    :key="r.id"
                    class="cs__range"
                  >
                    <input v-model="r.from" type="time" class="cs__tinp" />
                    <span>–</span>
                    <input v-model="r.to" type="time" class="cs__tinp" />
                    <button
                      type="button"
                      class="cs__icon-btn"
                      :title="t('collectSlots.remove')"
                      @click="removeRange(d, r.id)"
                    >
                      <i class="pi pi-times" />
                    </button>
                  </div>
                </template>
                <span v-else class="cs__muted">{{
                  t('collectSlots.noCollect')
                }}</span>
                <button
                  type="button"
                  class="cs__btn cs__btn--sm"
                  @click="addRange(d)"
                >
                  <i class="pi pi-plus" style="font-size: 0.7rem" />
                  {{ t('collectSlots.range') }}
                </button>
                <span v-if="!openingHours[d].length" class="cs__warn">
                  <i class="pi pi-info-circle" style="font-size: 0.7rem" />
                  {{ t('collectSlots.closedDay') }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div class="cs__sub-lbl" style="margin-top: 16px">
          {{ t('collectSlots.slicing') }}
        </div>
        <div class="cs__editor">
          <div class="cs__sec">
            <label class="cs__checkbox">
              <input
                v-model="cfg.slice"
                type="checkbox"
                class="cs__checkbox-input"
              />
              <span class="cs__checkbox-box"><i class="pi pi-check" /></span>
              {{ t('collectSlots.sliceToggle') }}
            </label>

            <p v-if="!cfg.slice" class="cs__sub" style="margin: 6px 0 0">
              {{ t('collectSlots.sliceOffPre') }}
              <b>{{ t('collectSlots.sliceOffBold') }}</b>
              {{ t('collectSlots.sliceOffPost') }}
            </p>

            <template v-else>
              <div class="cs__row" style="margin-top: 10px">
                <span class="cs__lbl">{{
                  t('collectSlots.stepInterval')
                }}</span>
                <span class="cs__chip-row">
                  <button
                    v-for="n in STEP_OPTIONS"
                    :key="n"
                    type="button"
                    class="cs__chip"
                    :class="{ 'cs__chip--active': cfg.step === n }"
                    @click="cfg.step = n"
                  >
                    {{ n }} {{ t('collectSlots.min') }}
                  </button>
                </span>
              </div>
              <p class="cs__sub" style="margin: 6px 0 8px">
                {{ t('collectSlots.sliceOnPre') }}
                <b>{{ t('collectSlots.sliceOnBold') }}</b>
                {{ t('collectSlots.sliceOnMid') }} {{ cfg.step }}
                {{ t('collectSlots.sliceOnPost') }}
              </p>
              <div class="cs__preview">
                <div
                  v-for="d in WD7.filter((d) => effRangesForDay(d).length)"
                  :key="d"
                  class="cs__sched-row"
                >
                  <span class="cs__d">{{ wdShort(d) }}</span>
                  <span class="cs__muted">
                    {{
                      effRangesForDay(d)
                        .map((r) => `${r.from}–${r.to}`)
                        .join(' · ')
                    }}
                    · <b>{{ dayCount(d) }}</b> {{ t('collectSlots.slots') }}
                  </span>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- 2 · Disponibilité des commandes -->
      <div class="cs__param">
        <div class="cs__param-h">{{ t('collectSlots.step2Title') }}</div>
        <div class="cs__types">
          <button
            v-for="m in SLOT_MODES"
            :key="m.id"
            type="button"
            class="cs__type"
            :class="{ 'cs__type--on': cfg.mode === m.id }"
            @click="cfg.mode = m.id"
          >
            <span class="cs__type-ic"><i :class="['pi', m.icon]" /></span>
            <span class="cs__type-tx">
              <b>{{ t(m.labelKey) }}</b>
              <span>{{ t(m.descKey) }}</span>
            </span>
          </button>
        </div>

        <div class="cs__editor">
          <!-- ASAP -->
          <div v-if="cfg.mode === 'asap'" class="cs__sec">
            <div class="cs__row">
              <span class="cs__lbl">{{ t('collectSlots.minPrepTime') }}</span>
              <span>
                <input
                  v-model.number="cfg.asap.prep"
                  type="number"
                  min="1"
                  max="240"
                  class="cs__ninp"
                />
                {{ t('collectSlots.min') }}
              </span>
            </div>
            <div class="cs__warn-banner">
              <i class="pi pi-info-circle" />
              {{ t('collectSlots.acceptOnlyDuringHours') }}
            </div>
            <p class="cs__sub" style="margin: 8px 0 0">
              {{ t('collectSlots.asapExplainPre') }}
              <b
                >{{ t('collectSlots.asapExplainBoldPre') }} {{ cfg.asap.prep }}
                {{ t('collectSlots.min') }}</b
              >
              {{ t('collectSlots.asapExplainPost') }}
            </p>
          </div>

          <!-- Programmé -->
          <div v-else class="cs__sec">
            <p class="cs__sub" style="margin: 0 0 10px">
              {{ t('collectSlots.scheduledExplainPre') }}
              <b>{{ t('collectSlots.scheduledExplainDay') }}</b>
              {{ t('collectSlots.scheduledExplainMid') }}
              <b>{{ t('collectSlots.scheduledExplainCutoff') }}</b>
              {{ t('collectSlots.scheduledExplainPost') }}
            </p>
            <div
              v-for="d in openDays"
              :key="d"
              class="cs__sched-line"
              :class="{ 'cs__sched-line--off': !scheduledDayOf(d).enabled }"
            >
              <label class="cs__checkbox" style="margin: 0">
                <input
                  v-model="scheduledDayOf(d).enabled"
                  type="checkbox"
                  class="cs__checkbox-input"
                />
                <span class="cs__checkbox-box"><i class="pi pi-check" /></span>
                <b
                  >{{ t('collectSlots.pickup') }}
                  {{ wdShort(d).toLowerCase() }}</b
                >
              </label>
              <span class="cs__muted">{{ t('collectSlots.orderBefore') }}</span>
              <input
                v-model.number="scheduledDayOf(d).cutoffDays"
                type="number"
                min="0"
                max="30"
                class="cs__ninp"
                :disabled="!scheduledDayOf(d).enabled"
              />
              <span class="cs__muted">{{ t('collectSlots.daysBefore') }}</span>
              <input
                v-model="scheduledDayOf(d).cutoffTime"
                type="time"
                class="cs__tinp"
                :disabled="!scheduledDayOf(d).enabled"
              />
              <span
                v-if="scheduledDayOf(d).enabled"
                class="cs__muted cs__cutoff-hint"
              >
                {{ t('collectSlots.cutoffFallsOn') }}
                <b>{{
                  wdShort(
                    cutoffWeekday(d, scheduledDayOf(d).cutoffDays),
                  ).toLowerCase()
                }}</b>
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Capacité -->
      <div class="cs__param cs__param--cap">
        <label class="cs__checkbox">
          <input
            v-model="cfg.capacity.enabled"
            type="checkbox"
            class="cs__checkbox-input"
          />
          <span class="cs__checkbox-box"><i class="pi pi-check" /></span>
          {{ t('collectSlots.limitOrdersPer') }} {{ capacityUnit }}
        </label>
        <span v-if="cfg.capacity.enabled" class="cs__cap-row">
          <input
            v-model.number="cfg.capacity.max"
            type="number"
            min="1"
            max="99"
            class="cs__ninp"
          />
          {{ t('collectSlots.maxOrdersPer') }} {{ capacityUnit }}
        </span>
      </div>

      <!-- Combinaisons : masquées pour le moment (mock non branché serveur) -->
      <div v-if="false" class="cs__param cs__param--combos">
        <div class="cs__combos-h">
          <b>{{ t('collectSlots.combinations') }}</b>
          <span class="cs__muted">{{
            t('collectSlots.combinationsDesc')
          }}</span>
        </div>
        <div v-for="k in cfg.combos" :key="k.id" class="cs__combo-line">
          <span class="cs__muted">{{ t('collectSlots.order') }}</span>
          <input v-model="k.orderFrom" type="time" class="cs__tinp" />
          <span class="cs__muted">–</span>
          <input v-model="k.orderTo" type="time" class="cs__tinp" />
          <span class="cs__muted">{{ t('collectSlots.toPickup') }}</span>
          <input v-model="k.pickFrom" type="time" class="cs__tinp" />
          <span class="cs__muted">–</span>
          <input v-model="k.pickTo" type="time" class="cs__tinp" />
          <button
            type="button"
            class="cs__icon-btn"
            :title="t('collectSlots.remove')"
            @click="removeCombo(k.id)"
          >
            <i class="pi pi-times" />
          </button>
        </div>
        <button type="button" class="cs__btn cs__btn--sm" @click="addCombo">
          <i class="pi pi-plus" style="font-size: 0.7rem" />
          {{ t('collectSlots.addCombo') }}
        </button>
      </div>

      <div v-if="saveError" class="cs__banner cs__banner--error">
        <i class="pi pi-exclamation-triangle" />
        {{ saveError }}
      </div>

      <div class="cs__autosave-status">
        <i v-if="saving" class="pi pi-spin pi-spinner" />
        <i v-else-if="saved" class="pi pi-check-circle cs__autosave-ok" />
        <span>{{
          saving
            ? t('collectSlots.saving')
            : saved
              ? t('collectSlots.saved')
              : ''
        }}</span>
      </div>
    </template>
  </div>
</template>

<style scoped>
.cs {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}
.cs__intro {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
  line-height: 1.5;
}

.cs__param {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--nxt-outline-variant);
}
.cs__param:last-child {
  border-bottom: none;
  padding-bottom: 0;
}
.cs__param-h {
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--nxt-on-surface-muted);
  margin-bottom: 0.25rem;
}
.cs__sub-lbl {
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}

.cs__types {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 0.625rem;
}
.cs__type {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.75rem 0.875rem;
  border: 1.5px solid var(--nxt-outline-variant);
  border-radius: 0.75rem;
  background: var(--nxt-card-bg);
  cursor: pointer;
  text-align: left;
  font-family: inherit;
}
.cs__type--on {
  border-color: #ffd60a;
  background: rgba(255, 214, 10, 0.08);
}
.cs__type-ic {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  flex-shrink: 0;
  border-radius: 0.5rem;
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface-variant);
}
.cs__type--on .cs__type-ic {
  background: #ffd60a;
  color: #0b1320;
}
.cs__type-tx {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  font-size: 0.8125rem;
}
.cs__type-tx b {
  color: var(--nxt-on-surface);
}
.cs__type-tx span {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
}

.cs__editor {
  background: var(--nxt-surface-low);
  border-radius: 0.75rem;
  padding: 0.875rem;
}
.cs__sec {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.cs__sub {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant);
  line-height: 1.5;
}
.cs__muted {
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}
.cs__warn {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  color: var(--nxt-error, #ef4444);
}
.cs__warn-banner {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  border-radius: 0.625rem;
  background: rgba(255, 214, 10, 0.1);
  color: #8a6d00;
  font-size: 0.8125rem;
  font-weight: 600;
}

.cs__preview {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.cs__sched-row {
  display: flex;
  align-items: baseline;
  gap: 0.625rem;
}
.cs__d {
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
  min-width: 2.5rem;
}

.cs__autosplit {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.75rem;
  border: 1px dashed var(--nxt-outline-variant);
  border-radius: 0.625rem;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant);
  flex-wrap: wrap;
}
.cs__ninp {
  width: 3.5rem;
  height: 2rem;
  padding: 0 0.5rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.5rem;
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface);
  font-size: 0.8125rem;
  font-family: inherit;
}

.cs__day {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.5rem 0;
  border-bottom: 1px dashed var(--nxt-outline-variant);
}
.cs__day:last-child {
  border-bottom: none;
}
.cs__day-h {
  width: 2.5rem;
  flex-shrink: 0;
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
  padding-top: 0.375rem;
}
.cs__ranges {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.cs__range {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}
.cs__tinp {
  height: 2rem;
  padding: 0 0.5rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.5rem;
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface);
  font-size: 0.8125rem;
  font-family: inherit;
}
.cs__sel-sm {
  height: 2rem;
  padding: 0 0.5rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.5rem;
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface);
  font-size: 0.8125rem;
  font-family: inherit;
  cursor: pointer;
}
.cs__icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 50%;
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface-variant);
  font-size: 0.7rem;
  cursor: pointer;
  flex-shrink: 0;
}
.cs__icon-btn:hover {
  border-color: #ef4444;
  color: #ef4444;
}

.cs__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  height: 2rem;
  padding: 0 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface-variant);
  font-size: 0.75rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
}
.cs__btn--sm {
  height: 1.875rem;
}
.cs__btn:hover {
  border-color: #ffd60a;
}
.cs__btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.cs__loading {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1.5rem 0;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}
.cs__banner {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  font-size: 0.8125rem;
  font-weight: 600;
}
.cs__banner--error {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}
.cs__banner--success {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}
.cs__autosave-status {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.4rem;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
  min-height: 1.25rem;
}
.cs__autosave-ok {
  color: #10b981;
}

.cs__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
}
.cs__lbl {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--nxt-on-surface-variant);
}
.cs__chip-row {
  display: flex;
  gap: 0.375rem;
  flex-wrap: wrap;
}
.cs__chip {
  padding: 0.3rem 0.75rem;
  border-radius: 9999px;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface-variant);
  font-size: 0.75rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
}
.cs__chip--active {
  border-color: #ffd60a;
  background: rgba(255, 214, 10, 0.12);
  color: #0b1320;
  font-weight: 700;
}

.cs__checkbox {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
  user-select: none;
}
.cs__checkbox-input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}
.cs__checkbox-box {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.125rem;
  height: 1.125rem;
  flex-shrink: 0;
  border: 1.5px solid var(--nxt-outline-variant);
  border-radius: 0.3rem;
  background: var(--nxt-card-bg);
  transition:
    background 0.15s,
    border-color 0.15s;
}
.cs__checkbox-box i {
  font-size: 0.625rem;
  font-weight: 900;
  color: #0b1320;
  opacity: 0;
  transform: scale(0.6);
  transition:
    opacity 0.15s,
    transform 0.15s;
}
.cs__checkbox-input:checked + .cs__checkbox-box {
  background: #ffd60a;
  border-color: #ffd60a;
}
.cs__checkbox-input:checked + .cs__checkbox-box i {
  opacity: 1;
  transform: scale(1);
}

.cs__sched-line {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.5rem 0;
  border-bottom: 1px dashed var(--nxt-outline-variant);
  flex-wrap: wrap;
}
.cs__sched-line:last-child {
  border-bottom: none;
}
.cs__sched-line--off {
  opacity: 0.55;
}

.cs__param--cap {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  border-bottom: 1px solid var(--nxt-outline-variant);
  padding-bottom: 1rem;
}
.cs__cap-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant);
}

.cs__param--combos {
  border-bottom: none;
}
.cs__combos-h {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 0.5rem;
}
.cs__combos-h b {
  font-size: 0.8125rem;
  color: var(--nxt-on-surface);
}
.cs__combo-line {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0;
  flex-wrap: wrap;
}
</style>

<style>
[data-theme='dark'] .cs__type,
[data-theme='dark'] .cs__tinp,
[data-theme='dark'] .cs__sel-sm,
[data-theme='dark'] .cs__ninp,
[data-theme='dark'] .cs__icon-btn,
[data-theme='dark'] .cs__btn,
[data-theme='dark'] .cs__chip,
[data-theme='dark'] .cs__checkbox-box {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: #fff;
}
[data-theme='dark'] .cs__type--on {
  background: rgba(255, 214, 10, 0.12);
  border-color: #ffd60a;
}
[data-theme='dark'] .cs__chip--active {
  background: rgba(255, 214, 10, 0.15);
  color: #ffd60a;
}
[data-theme='dark'] .cs__checkbox-input:checked + .cs__checkbox-box {
  background: #ffd60a;
  border-color: #ffd60a;
}
[data-theme='dark'] .cs__editor {
  background: rgba(255, 255, 255, 0.04);
}
[data-theme='dark'] .cs__warn-banner {
  background: rgba(255, 214, 10, 0.12);
  color: #ffd60a;
}
</style>
